import React from 'react';
import { motion } from 'framer-motion';

const AboutUs = () => {
  return (
    <div className="relative min-h-screen bg-gradient-to-b from-white to-gray-100 text-gray-900 overflow-hidden">
      {/* Glass DNA Strip */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 h-full w-2 backdrop-blur-sm bg-gradient-to-b from-blue-300/30 via-purple-300/20 to-transparent rounded-full pointer-events-none"></div>

        {/* Floating biotech particles */}
        {[...Array(50)].map((_, i) => {
          const size = Math.random() * 10 + 6;
          const left = Math.random() * 100;
          const top = Math.random() * 100;
          const duration = Math.random() * 20 + 10;
          const delay = Math.random() * 5;
          return (
            <div
              key={`float-${i}`}
              className="absolute bg-gradient-to-r from-blue-300 to-purple-300 rounded-full opacity-30"
              style={{
                width: `${size}px`,
                height: `${size}px`,
                left: `${left}%`,
                top: `${top}%`,
                animation: `floatY ${duration}s ease-in-out infinite`,
                animationDelay: `${delay}s`,
              }}
            />
          );
        })}
      </div>

      {/* Main content */}
      <div className="relative z-10 container mx-auto px-6">
        {/* Hero */}
        <section id ="about" className="pt-28 pb-16 text-center">
          <motion.h1
            className="text-5xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-blue-700 to-purple-700"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            UTR Therapeutics Inc.
          </motion.h1>
          <motion.p
            className="text-xl text-gray-600 mt-4 max-w-2xl mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            IND-stage biotech with first-in-class RNA rewriting technology.
          </motion.p>
        </section>

        {/* About Section */}
        <motion.section
          className="bg-white/80 backdrop-blur-md border border-gray-200/30 shadow-xl rounded-3xl p-10 mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
            About The Company
          </h2>
          <div className="text-gray-800 text-lg leading-relaxed space-y-6">
            <p>
              UTR Therapeutics Inc. is an IND-stage biotech company pioneering a transformative RNA overwriting platform that can selectively downregulate any transcript of interest—without genome editing or rearrangement.
            </p>
            <p>
              Our platform has demonstrated robust preclinical efficacy in hard-to-treat cancers including c-MYC driven tumors and HER2-expressing drug-resistant variants.
            </p>
            <p>
              We’re now expanding into neurodegenerative and rare disease applications with a scalable, modular therapeutic pipeline.
            </p>
          </div>
        </motion.section>

        {/* Mission & Values */}
        <div className="grid md:grid-cols-2 gap-8 pb-24">
          {/* Mission */}
          <motion.div
            className="bg-white/80 backdrop-blur-md border border-gray-200/30 rounded-2xl shadow-lg p-8"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h3 className="text-2xl font-semibold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
              Our Mission
            </h3>
            <p className="text-gray-700 text-lg">
              To fundamentally change how we treat disease by rewriting biology at the RNA level—targeted, precise, and safe.
            </p>
          </motion.div>

          {/* Values */}
          <motion.div
            className="bg-white/80 backdrop-blur-md border border-gray-200/30 rounded-2xl shadow-lg p-8"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h3 className="text-2xl font-semibold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
              UTR Values
            </h3>
            <ul className="text-gray-700 text-lg space-y-3 list-disc list-inside">
              <li>Diversity and inclusion across science and leadership.</li>
              <li>Scientific rigor with compassion for patients.</li>
              <li>Transparency, trust, and long-term impact.</li>
            </ul>
          </motion.div>
        </div>
      </div>

      {/* Global Animations */}
      <style>{`
        @keyframes floatY {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
          100% { transform: translateY(0px); }
        }
      `}</style>
    </div>
  );
};

export default AboutUs;
